/**
 * LanguageSelector — dropdown replacement for 34+ languages.
 * Overrides Volto's flat pipe-separated list with a globe dropdown.
 * DR-2026-054: Vilax exhibition requires 34 languages.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';

const langNames = {
  en: 'English', sv: 'Svenska', de: 'Deutsch', zh: '中文',
  bg: 'Български', cs: 'Čeština', da: 'Dansk', el: 'Ελληνικά',
  es: 'Español', et: 'Eesti', fi: 'Suomi', fr: 'Français',
  ga: 'Gaeilge', hr: 'Hrvatski', hu: 'Magyar', it: 'Italiano',
  lt: 'Lietuvių', lv: 'Latviešu', mt: 'Malti', nl: 'Nederlands',
  pl: 'Polski', pt: 'Português', ro: 'Română', sk: 'Slovenčina',
  sl: 'Slovenščina', ko: '한국어', hi: 'हिंदी', ta: 'தமிழ்',
  ja: '日本語', id: 'Indonesia', ms: 'Melayu', tl: 'Filipino',
  th: 'ไทย', sw: 'Kiswahili',
};

const LanguageSelector = ({ onClickAction = () => {} }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );
  const isMultilingual = useSelector(
    (state) => state.site.data.features?.multilingual,
  );
  const availableLanguages = useSelector(
    (state) => state.site.data?.['plone.available_languages'],
  );

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    if (open) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  if (!isMultilingual) {
    return <Helmet><html lang={toReactIntlLang(currentLang)} /></Helmet>;
  }

  const currentName = langNames[currentLang] || currentLang;

  return (
    <div className="language-selector" ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          fontSize: '0.9rem',
          fontWeight: 600,
          color: 'inherit',
          textTransform: 'uppercase',
        }}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Select language"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10A15.3 15.3 0 0 1 12 2z" />
        </svg>
        {currentName}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          zIndex: 1000,
          background: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          padding: '8px 0',
          minWidth: '200px',
          maxHeight: '70vh',
          overflowY: 'auto',
          marginTop: '4px',
        }}>
          {availableLanguages?.map((lang) => {
            const translation = translations?.find((t) => t.language === lang);
            const name = langNames[lang] || lang;
            const isCurrent = toReactIntlLang(lang) === currentLang;
            return (
              <Link
                key={lang}
                to={translation ? flattenToAppURL(translation['@id']) : `/${lang}`}
                onClick={() => { setOpen(false); onClickAction(); }}
                style={{
                  display: 'block',
                  padding: '8px 16px',
                  color: isCurrent ? '#0b552a' : '#333',
                  fontWeight: isCurrent ? 600 : 400,
                  fontSize: '0.9rem',
                  textDecoration: 'none',
                  backgroundColor: isCurrent ? '#f0f7f2' : 'transparent',
                  transition: 'background-color 0.15s',
                }}
                onMouseEnter={(e) => { if (!isCurrent) e.target.style.backgroundColor = '#f5f5f5'; }}
                onMouseLeave={(e) => { if (!isCurrent) e.target.style.backgroundColor = 'transparent'; }}
                aria-label={`Switch to ${name}`}
              >
                {name}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
